import * as _ from "lodash";
import * as React from 'react';
import {translate} from "react-i18next";
import {connect} from "react-redux";
import {Checkbox, Icon, MenuItemProps} from "semantic-ui-react";
import {ITranslatedComponent} from "../../redux/reducers/generalReducer";
import {EColumnKey} from "../OptionsView/OptionsViewUtils";
import {EOBColumnKey} from "./BaseOptionsTableUtils";
import {ILocalStorageUserProperties, UserDynTableProperty} from "../../services/UserDynTableProperties";
import {EDefaultTableSortingKeys, ITableSort} from "../../utils/SortUtils";
import {
    HeaderFilterType,
    ILocalColumnSizeSettings,
    ITextExportStatus,
    IVirtualizedTableColumn,
    IVisibleTableColumnList,
    TData,
    TDataValue
} from "../../components/Table/VirtualizedTableInterfaces";
import {FilterHandler} from "../../components/Table/dataHandler/FilterHandler";
import {buildComponent} from "../../components/Table/ComponentBuilder";
import {AutoSizer, GridCellProps, Index, MultiGrid as ReactVirtualizedGrid} from "react-virtualized";
import TableFooterActions from "../../components/Table/components/footerComponents/TableFooterActions";
import TablePagination from "../../components/Table/components/footerComponents/pagination/TablePagination";
import {CategoryTextKey, LongtextKey, OptionTypeColumnKey, ShorttextKey, sortOBColumns, sortOBColumnsDropdown, StatusColumnKey} from "./BaseOptionsColumns";
import {
    buildTextExportStatusComponent,
    computeRowHeightInFullView,
    keepRowSorting,
    PCCDFlag,
    PDiPFlag,
    scrollGridToTop,
    virtualizedTableId
} from "../../components/Table/VirtualizedTableUtils";
import {PCCDFilterEnum, PDiPFilterEnum} from "../../components/Table/VirtualizedTableEnums";
import VirtualizedTableHeaderCell from "../../components/Table/components/headerComponents/virtualizedTableHeaderCell/VirtualizedTableHeaderCell";
import GeneralActions from "../../redux/actions/GeneralActions";
import BaseOptionViewActions from "../../redux/actions/BaseOptionViewActions";
import {compose, Dispatch} from "redux";
import {sortCaseInsensitive} from "../../components/Table/dataHandler/Utils";
import {CommentKey} from "../OptionsView/OptionColumns";
import {selectClient, selectOptionBasicTableSort, selectVirtualizedTableReload, selectVirtualizedTableRollback, selectVirtualizedTableSelectedIdList} from "../../redux/selectors";
import {IReduxState} from "src/redux/IReduxState";
import PCCDTextComparePopup from "../OptionsView/PCCDTextComparePopup";
import {MenuDto, WorkflowStatusDtoType} from "../../services/Client";
import HATEOASClient from "src/services/HATEOASClient";
import {getDeAiFlags} from "src/components/Table/components/twoLanguage/AWSTranslatePreviewHandler";

interface IMapDispatchToProps {
    setSelectedIdLists: (ids: string[]) => void;
    setTableSort: (sort: ITableSort) => void;
    setTableRollBack: (flag: boolean) => void;
}

interface IMapStateToProps {
    optionBasicTableSort: ITableSort;
    dynTableSelectedIdList: string[];
    dynTabelRollback: boolean;
    dynTableReload: boolean;
    client?: HATEOASClient;
    baseLinks?: MenuDto;

}

interface IOwnProps {
    columnsData: IVirtualizedTableColumn[];
    data: TData[];
    onChange: (key: string, data: TDataValue, index: number) => void;
    onChangeSelect: (index: number[]) => void;
    isCellReadOnly: (key: string, data: TData, readOnly: boolean) => boolean;
    workFlowResetStateDisabled: (key: string, data: TData, readOnly: boolean) => boolean;
    inEditMode: boolean;
    onTriggerClick: (index: string[]) => void;
    resourceLinks?: any;
}

export interface BaseOptionsTableProps extends ITranslatedComponent, IMapStateToProps, IMapDispatchToProps, IOwnProps { }

interface BaseOptionsTableState {
    data: TData[];
    filterValue: {[key: string]: any};
    currentPage: number;
    numberOfItems: number;
    visibleColumns: IVirtualizedTableColumn[];
    columnSizeSetting: ILocalColumnSizeSettings;
    rowVisibleList: IVisibleTableColumnList;
    // true for full view mode, false for normal mode
    isFullViewMode: boolean;
    rowSort: string[];
    activeRow: string | null;
    rows: IVirtualizedTableColumn[];
    dropdownFilters: {
        [OptionTypeColumnKey]: Set<string>,
        [StatusColumnKey]: Set<string>,
        [CategoryTextKey]: Set<string>,
    };
}

class BaseOptionsTable extends React.Component<BaseOptionsTableProps, BaseOptionsTableState> {
    gridRef: ReactVirtualizedGrid | null;

    private filterHandler: FilterHandler;
    private localStorageKey = "prompt_dynTable";
    private dataForRollBack: TData[];

    private userPropertiesSettings: UserDynTableProperty;

    constructor(props: BaseOptionsTableProps) {
        super(props);
        this.filterHandler = new FilterHandler();

        this.state = {
            data: [],
            filterValue: {},
            currentPage: 1,
            numberOfItems: 10,
            rowVisibleList: {},
            visibleColumns: [],
            columnSizeSetting: {},
            isFullViewMode: false,
            rowSort: [],
            rows: [],
            activeRow: null,
            dropdownFilters: {
                [OptionTypeColumnKey]: new Set<string>(),
                [StatusColumnKey]: new Set<string>(),
                [CategoryTextKey]: new Set<string>(),
            },
        };

        this.userPropertiesSettings = new UserDynTableProperty(this.localStorageKey + "_BaseOption", this.props.columnsData);
    }

    componentDidMount() {
        this.setState({rows: this.props.columnsData}, () => {
            const filterValue: {[key: string]: boolean | string} = {};

            let localUserProperties: ILocalStorageUserProperties | null = this.userPropertiesSettings.get();

            if (localUserProperties === null) {
                localUserProperties = this.userPropertiesSettings.create();
                this.userPropertiesSettings.save(localUserProperties);
            }

            this.createDropdownFilterValues(this.props.data);

            this.setState({
                data: this.props.data,
                filterValue: filterValue,
                rowSort: localUserProperties ? localUserProperties.rowSort : [],
                rowVisibleList: localUserProperties ? localUserProperties.rowVisibleList : {},
                columnSizeSetting: localUserProperties ? localUserProperties.columnSizeSetting : {}
            });
        });
    }

    componentDidUpdate(prevProps: BaseOptionsTableProps, prevState: BaseOptionsTableState) {
        if (prevProps.columnsData !== this.props.columnsData) {
            this.setState(
                {
                    rows: keepRowSorting(this.state.rowSort, this.props.columnsData),
                },
                () => this.setState({
                    visibleColumns: this.state.rows.filter(column => this.state.rowVisibleList[column.key].visible).sort(sortOBColumns)
                })
            );
        }
        if (prevProps.data !== this.props.data) {
            this.setState({data: this.props.data});
            this.createDropdownFilterValues(this.props.data);
        }

        if (this.props.dynTableReload === true && prevProps.dynTableReload === false) {
            this.setState({filterValue: {}});
        }

        let localUserProperties: ILocalStorageUserProperties | null = this.userPropertiesSettings.get();

        if (localUserProperties) {
            if (!_.isEqual(localUserProperties.columnSizeSetting, this.state.columnSizeSetting) ||
                !_.isEqual(localUserProperties.rowVisibleList, this.state.rowVisibleList) ||
                !_.isEqual(localUserProperties.rowSort, this.state.rowSort)) {
                let updatedUserProperties: ILocalStorageUserProperties | null = this.userPropertiesSettings.update(
                    this.state.rows,
                    this.state.columnSizeSetting,
                    this.state.rowVisibleList,
                    this.state.rowSort
                );

                if (updatedUserProperties) {
                    this.setState(
                        {
                            rowSort: updatedUserProperties.rowSort,
                            rowVisibleList: updatedUserProperties.rowVisibleList,
                            columnSizeSetting: updatedUserProperties.columnSizeSetting
                        },
                        () => {
                            this.userPropertiesSettings.save(updatedUserProperties);
                            this.setState({
                                visibleColumns: this.state.rows.filter(column =>
                                    this.state.rowVisibleList[column.key] &&
                                    this.state.rowVisibleList[column.key].visible).sort(sortOBColumns)
                            });
                        }
                    );
                }
            }
        }

        if (this.props.dynTabelRollback) {
            this.createDropdownFilterValues(this.props.data);
            this.setState({data: this.dataForRollBack});
            this.props.setTableRollBack(false);
        }

        if (this.gridRef) {
            this.gridRef.forceUpdate();
            this.gridRef.recomputeGridSize();
        }
    }

    componentWillUnmount() {
        this.props.setSelectedIdLists([]);
    }

    private createDropdownFilterValues = (data: TData[]) => {
        const {filterValue} = this.state;

        const optionTypes: Set<string> = new Set<string>();
        const workflowTypes: Set<string> = new Set<string>();
        const categoryTypes: Set<string> = new Set<string>();

        data.map(option => {
            if (option.optionType) {
                // @ts-ignore
                optionTypes.add(option.optionType.optionType);
            }

            if (option.categoryText) {
                if (typeof option.categoryText === 'string') {
                    categoryTypes.add(option.categoryText);
                } else {
                    // @ts-ignore
                    categoryTypes.add(option.categoryText ? option.categoryText.categoryText : '');
                }
            }

            // @ts-ignore
            const workflowStepName = option.statusView && option.statusView.statusValueStates && option.statusView.statusValueState
                // @ts-ignore
                && option.statusView.statusValueStates.find(state => state.id === option.statusView.statusValueState);
            if (workflowStepName) {
                // the hover text contains also the reject message
                workflowTypes.add(workflowStepName.hoverText.split(" - ")[0] as string);
            }
        });

        // clean filter value if no longer available
        if (filterValue[OptionTypeColumnKey]) {
            filterValue[OptionTypeColumnKey].forEach((value: string) => {
                if (!optionTypes.has(value)) {
                    filterValue[OptionTypeColumnKey].delete(value);
                }
            });
        }

        if (filterValue[CategoryTextKey]) {
            filterValue[CategoryTextKey].forEach((value: string) => {
                if (!categoryTypes.has(value)) {
                    filterValue[CategoryTextKey].delete(value);
                }
            });
        }

        if (filterValue[StatusColumnKey]) {
            filterValue[StatusColumnKey].forEach((value: string) => {
                if (!workflowTypes.has(value)) {
                    filterValue[StatusColumnKey].delete(value);
                }
            });
        }

        this.setState({
            dropdownFilters: {
                [OptionTypeColumnKey]: new Set(Array.from(optionTypes).sort()),
                [StatusColumnKey]: new Set(Array.from(workflowTypes).sort(sortCaseInsensitive)),
                [CategoryTextKey]: categoryTypes
            },
            filterValue: filterValue
        });
    }

    /**
     * Selects all items
     */
    private selectAll(checked: boolean, filteredData: TData[]) {
        let selectedIdSet = new Set(this.props.dynTableSelectedIdList);
        // select or deselect all filtered data (works the same if no filter is set)

        filteredData.forEach((entry: TData) => {
            if (entry._id) {
                if (checked) {
                    selectedIdSet.add(entry._id);
                } else {
                    selectedIdSet.delete(entry._id);
                }
            }
        });

        const selectedIdList = Array.from(selectedIdSet);
        const indexList = selectedIdList.map((item: string) => parseInt(item, 10));

        // TODO the select ids are stored in 2 places. This should be refactored
        this.props.setSelectedIdLists(selectedIdList);
        this.props.onChangeSelect(indexList);
    }

    /**
     * processes the change to the selection array
     * @param checked is the row selected
     * @param rowNumber target rowNumber
     */
    private onChangeRowSelection(checked: boolean, rowId: string) {
        let selectedIdList = [... this.props.dynTableSelectedIdList];

        if (checked) {
            selectedIdList.push(rowId);
        }
        else {
            _.pull(selectedIdList, rowId);
        }
        const indexList = selectedIdList.map((item) => parseInt(item, undefined));
        this.props.setSelectedIdLists(selectedIdList);
        this.props.onChangeSelect(indexList);
    }

    /**
     * on cell value changed
     */
    private onDataChanged(
        key: string,
        rowNumber: number,
        newValue: any,
    ) {
        const data = [...this.state.data];
        this.dataForRollBack = _.cloneDeep(data);
        if (!_.isEqual(data[rowNumber][key], newValue)) {
            data[rowNumber][key] = newValue;

            const row = data[rowNumber] as any;
            let aiMeta: any = null;

            if (newValue) {
                for (const k in newValue) {
                    if (!Object.prototype.hasOwnProperty.call(newValue, k)) {
                        continue;
                    }

                    const v = newValue[k];

                    if (v && v.aiMeta) {
                        aiMeta = v.aiMeta;
                        break;
                    }
                }
            }

            if (
              aiMeta &&
              row &&
              row.references &&
              row.references.dto &&
              Array.isArray(row.references.dto.texts)
            ) {
              const texts = row.references.dto.texts;

              for (let i = 0; i < texts.length; i++) {
                const t = texts[i];

                if (aiMeta.aiGeneratedShorttext !== undefined) {
                    t.aiGeneratedShorttext = aiMeta.aiGeneratedShorttext;
                }

                if (aiMeta.aiGeneratedLongtext !== undefined) {
                    t.aiGeneratedLongtext = aiMeta.aiGeneratedLongtext;
                }
              }
            }

            if (
            !aiMeta &&
            row &&
            row.references &&
            row.references.dto &&
            Array.isArray(row.references.dto.texts)
            ) {
            const texts = row.references.dto.texts;

            for (let i = 0; i < texts.length; i++) {
                const t = texts[i];

                if (key === "shorttext") {
                    t.aiGeneratedShorttext = false;
                }

                if (key === "longtext") {
                    t.aiGeneratedLongtext = false;
                }

            }
            }

            this.setState({ data }, () => {
              this.props.onChange(key, newValue, rowNumber);
            });
        }
    }

    private onFilterChange = (newHeaderFilter: HeaderFilterType) => {
        scrollGridToTop();
        this.setState({filterValue: newHeaderFilter, currentPage: 1});
    }

    private filterData = (data: TData[]) => {
        return this.filterHandler.filterData(data, this.state.filterValue, this.state.rows);
    }

    private onSort = (key: string, isAscending: boolean) => {
        const sortObject = {...this.props.optionBasicTableSort};
        if (key === EDefaultTableSortingKeys.categoryText || key === EDefaultTableSortingKeys.intSequence) {
            sortObject[key] = isAscending;
        }
        else {
            sortObject.thirdKey = key === sortObject.thirdKey && isAscending === sortObject.thirdKeyAsc ? undefined : key;
            sortObject.thirdKeyAsc = key === this.props.optionBasicTableSort.thirdKey
                && isAscending === this.props.optionBasicTableSort.thirdKeyAsc ? undefined : isAscending;
        }
        this.props.setTableSort(sortObject);
    }

    private handleTableMenuChange = (
        rowVisibleList: any,
        resetHideEmptyRowsAndColumnsFilter?: boolean,
        key?: string, clearHeaderFilters?: boolean
    ) => {

        if (clearHeaderFilters) {
            let newFilter = {...this.state.filterValue};
            Object.keys(this.state.filterValue).forEach(keyFilter => {
                if (keyFilter !== PDiPFlag) {
                    newFilter[keyFilter] = undefined;
                }
            });

            this.onFilterChange(newFilter);
        } else if (key && this.state.filterValue[key]) {
            this.onFilterChange({...this.state.filterValue, [key]: undefined});
        }

        this.setState({rowVisibleList}, () => {
            this.setState({
                rowSort: Object.keys(_.pickBy(this.state.rowVisibleList, (item) => item.visible))
            });
        });
    }

    private setActiveRow = (optionId: string) => {
        this.setState({activeRow: optionId});
    }

    private clearActiveRow = () => {
        this.setState({activeRow: null});
    }

    private setNumberOfItems = (e: React.MouseEvent<HTMLAnchorElement>, data: MenuItemProps) => {
        this.setState(
            {
                numberOfItems: data.value,
                currentPage: 1,
            },
            scrollGridToTop);
    }

    /**
     * Adds the _id and property
     */
    private appendAdditionalPropertiesToData = () => {
        let data: any[] = this.state.data.map((item, index) => {
            return {
                ...item,
                _id: index.toString(),
            };
        });
        return data;
    }

    /**
     *  Computes the number of visible columns so the Grid knows
     *  how many to render.
     */
    private getColumnCount = (): number => {
        return this.state.visibleColumns.length + 1;
    }

    /**
     *  Returns the width of the cells to the Grid according
     *  to the user settings.
     */
    private getColumnWidth = ({index}: Index): number => {
        if (index === 0) {
            return 70;
        }
        const trueIndex = index - 1;
        const {visibleColumns, columnSizeSetting} = this.state;
        return columnSizeSetting[visibleColumns[trueIndex].key] ? columnSizeSetting[visibleColumns[trueIndex].key].width + 20 : 120;
    }

    /**
     * Returns the height of the row.
     *
     * The header has a fixed height of 150, and the rows 120.
     * If full-view-mode is on, then CellMeasurer will be used.
     */
    private getRowHeight = ({index}: Index, filteredData: any[], columns: IVirtualizedTableColumn[]): number => {
        if (index === 0) {
            return 100;
        }
        return this.state.isFullViewMode
            ? filteredData[index - 1]
                ? computeRowHeightInFullView(filteredData[index - 1], columns, this.state.columnSizeSetting)
                : 100
            : 100;
    }

    private deSelectAll = () => {
        this.props.setSelectedIdLists([]);
        this.props.onChangeSelect([]);
    }

    /**
     *  Computes the cells for the Grid based on the indexes
     *  passed from the Grid renderer function.
     */
    private renderGridCell = (
        {columnIndex, key, rowIndex, style, parent}: GridCellProps,
        data: any[],
        filteredData: any[],
    ): React.ReactNode => {

        if (data.length === 0 && rowIndex === 1) {
            return <div className='grid-empty-row' key={key} style={style} />;
        }

        if (columnIndex === 0 && rowIndex === 0) {
            // true if all the filtered items are included in the selected items list (works the same if no filter is set)
            const checked = filteredData.every((td: TData) =>
                td._id !== undefined && this.props.dynTableSelectedIdList.includes(td._id));

            return (
                <div
                    className='grid-first-column-row  grid-border-left grid-border-bottom grid-border-right grid-border-top'
                    key={key}
                    style={{...style, paddingTop: 50}}
                >
                    <Checkbox
                        checked={checked}
                        slider={true}
                        onChange={(e, v) => this.selectAll(v.checked as boolean, filteredData)}
                    />
                    {this.props.dynTableSelectedIdList.length > 0 &&
                        <div style={{display: "flex", height: '10px', justifyContent: 'space-between'}}>
                            <a className="ui table-header-button active-dropdown-button circular label" style={{minWidth: 40, marginLeft: -5}}>
                                <p style={{fontSize: '10px'}}>{this.props.dynTableSelectedIdList.length}</p>
                            </a>
                            <a className="ui table-header-button circular dont label" onClick={() => this.deSelectAll()} style={{fontSize: 10}}>
                                <Icon name="dont" />
                            </a>
                        </div>
                    }
                </div>
            );
        }

        if (rowIndex === 0) {
            return this.renderGridHeader(columnIndex, key, style);
        }

        if (columnIndex === 0) {
            const item = data[rowIndex - 1];
            return (
                <div
                    className={`grid-centered-cell grid-border-left grid-border-bottom grid-border-right ${rowIndex % 2 === 1 ? 'grid-white-row' : 'grid-grey-row'}
                                ${item.rowClass === "" ? '' : 'table-invalid-pdip-first-cell'} ${item._id === this.state.activeRow ? 'table-active-row' : ''}`}
                    key={key}
                    style={style}
                    id={`checkbox-${rowIndex}`}
                >
                    <Checkbox
                        slider={true}
                        checked={this.props.dynTableSelectedIdList.findIndex((rowId) => data[rowIndex - 1]._id === rowId) > -1}
                        onChange={(e, v) => {
                            this.onChangeRowSelection(v.checked as boolean, data[rowIndex - 1]._id as string);
                        }}
                    />

                </div>
            );

        }

        return this.renderGridBody(rowIndex, columnIndex, key, style, data);
    }

    /**
     *  Computes the cells of the fixed row in the Grid (Header)
     */
    private renderGridHeader = (columnIndex: number, key: string, style: React.CSSProperties): React.ReactNode => {
        const item = this.state.visibleColumns[columnIndex - 1];
        let isAscending;

        if (this.state.rowVisibleList[item.key] && !this.state.rowVisibleList[item.key].visible)
            return null;

        if (item.key === EDefaultTableSortingKeys.categoryText || item.key === EDefaultTableSortingKeys.intSequence) {
            isAscending = this.props.optionBasicTableSort[item.key];
        } else if (this.props.optionBasicTableSort.thirdKey === item.key) {
            isAscending = this.props.optionBasicTableSort.thirdKeyAsc;
        }
        return (
            <VirtualizedTableHeaderCell
                t={this.props.t}
                key={key}
                hasDropdown={true}
                isAscending={isAscending}
                visibleColumn={item}
                style={style}
                columnSizeSetting={this.state.columnSizeSetting}
                filterValue={this.state.filterValue}
                setState={(newColumnWidth: ILocalColumnSizeSettings) => this.setState({columnSizeSetting: newColumnWidth})}
                onFilterChange={(filterKey, value) => this.onFilterChange({...this.state.filterValue, [filterKey]: value})}
                onSort={this.onSort}
                dropdownFilters={this.state.dropdownFilters}
            />
        );
    }

    /**
     *  Computes the cells of the Grid body
     */
    private renderGridBody = (
        rowIndex: number,
        columnIndex: number,
        keyGrid: string,
        style: React.CSSProperties,
        data: TData[],
    ): React.ReactNode => {

        const item: TData = data[rowIndex - 1];
        let readOnly: boolean = item.isDisabled !== undefined ? item.isDisabled as boolean : false;
        const visibleColumn: IVirtualizedTableColumn = this.state.visibleColumns[columnIndex - 1];
        const key: string = visibleColumn.key;
        const TargetComponent = buildComponent(visibleColumn.rowType);
        const options = visibleColumn.options || {};
        // this is an index, not the real option dto id
        const id: number = parseInt(item._id as string, 10);
        let activeRowClassName = '';
        if (item._id === this.state.activeRow) {
            activeRowClassName = 'table-active-row';
        }

        const className = `grid-centered-cell grid-border-bottom grid-border-right
        ${item.rowClass} ${activeRowClassName} ${rowIndex % 2 === 1 ? 'grid-white-row' : 'grid-grey-row'}`;

        if (!readOnly && options.readOnly !== null && options.readOnly !== undefined) {
            readOnly = options.readOnly;
        }

        if (key === EOBColumnKey.OptionCodeKey || key === EColumnKey.OptionBasicViewKey)
            return (
                <div
                    className={className}
                    key={keyGrid}
                    style={style}
                >
                    <div style={{display: "flex", alignItems: "center"}}>
                        {
                            !item.pccdFlag &&
                            <PCCDTextComparePopup
                                item={item}
                                t={this.props.t}
                                label={this.props.t("OPTION_BASIC_TEXT")}
                            />
                        }
                        {/* {
                            buildTextExportStatusComponent((item[EOBColumnKey.ExportStatus]) as ITextExportStatus)
                        } */}
                        <div style={{width: '100%'}}>
                            <TargetComponent
                                t={this.props.t}
                                rowIndex={rowIndex}
                                language={this.props.i18n.language}
                                uglyMode={this.state.isFullViewMode}
                                workflowReadOnly={this.props.workFlowResetStateDisabled(key, item, readOnly)}
                                value={item[key] as string}
                                onChange={(newValue) => this.onDataChanged(key, id, newValue)}
                                readOnly={readOnly ? readOnly : this.props.isCellReadOnly(key, item, readOnly)}
                                inEditMode={this.props.inEditMode}
                            />
                        </div>
                    </div>
                </div>
            );
        else if (key === EOBColumnKey.ShorttextKey) {
            const { aiGeneratedShorttext, aiGeneratedLongtext } = getDeAiFlags(item);
            return (
                <div
                    className={className}
                    key={keyGrid}
                    style={style}
                >
                    <div style={{display: "flex", alignItems: "center"}}>
                        <div style={{width: '100%'}}>
                            <TargetComponent
                                t={this.props.t}
                                rowIndex={rowIndex}
                                language={this.props.i18n.language}
                                uglyMode={this.state.isFullViewMode}
                                workflowReadOnly={this.props.workFlowResetStateDisabled(key, item, readOnly)}
                                value={item[key] as string}
                                onChange={(newValue) => this.onDataChanged(key, id, newValue)}
                                readOnly={readOnly ? readOnly : this.props.isCellReadOnly(key, item, readOnly)}
                                inEditMode={this.props.inEditMode}
                                setActiveRow={() => item._id && this.setActiveRow(item._id)}
                                clearActiveRow={this.clearActiveRow}
                                editDialogHeader={{
                                    dialogTitle: this.props.t('BASEOPTIONS:EDIT_BASIC_OPTION'),
                                    previewAiHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_HEADER'),
                                    previewAiSubHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_SUB_HEADER'),
                                    previewAiOriginal: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_ORIGINAL'),
                                    previewAiPreview: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_PREVIEW'),
                                    previewAiConfirm: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM'),
                                    previewAiCancel: this.props.t('GLOBAL:CANCEL'),
                                    previewAiConfimChange: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM_CHANGES'),
                                    previewAiGrammarSubHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_GRAMMAR_SUB_HEADER'),
                                    tooltipAiTranslate: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP'),
                                    tooltipAiGrammar:this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP_GRAMMAR'),
                                    editingColumn: this.state.visibleColumns[columnIndex - 1].label,
                                    optionCode: item.optionCode ? item.optionCode as string : "",
                                    // @ts-ignore
                                    optionType: item.optionType ? item.optionType.optionType as string : "",
                                    pdipText: item.pdiptext as string
                                }}
                                isEditor={false}
                                client={this.props.client}
                                postOptions={this.props.resourceLinks}
                                apiMethod={(client, postOptions) => client.POST_optionBasicAWSTranslate(postOptions)}
                                baseLinks={this.props.baseLinks}
                                grammarApiMethod={(client, unusedPostOptions) =>
                                    this.props.baseLinks
                                        ? client.POST_awsBedrockGrammar(this.props.baseLinks)
                                        : { execute: () => Promise.reject("Links not loaded") }
                                }
                                aiGeneratedShorttext={aiGeneratedShorttext}
                                aiGeneratedLongtext={aiGeneratedLongtext}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        else if (key === EColumnKey.StatusViewKey) {
            return (
                <div
                    className={className}
                    key={keyGrid}
                    style={style}
                >
                    <TargetComponent
                        t={this.props.t}
                        rowIndex={rowIndex}
                        language={this.props.i18n.language}
                        uglyMode={this.state.isFullViewMode}
                        workflowReadOnly={this.props.workFlowResetStateDisabled(key, item, readOnly)}
                        value={item[key] as string}
                        onChange={(newValue) => this.onDataChanged(key, id, newValue)}
                        readOnly={readOnly ? readOnly : this.props.isCellReadOnly(key, item, readOnly)}
                        inEditMode={this.props.inEditMode}
                    />
                </div>
            );
        } else {
            const { aiGeneratedShorttext, aiGeneratedLongtext } = getDeAiFlags(item);
            return (
                <div
                    className={className}
                    key={keyGrid}
                    style={style}
                    id={`${key}-${rowIndex}`}
                >
                    <TargetComponent
                        t={this.props.t}
                        rowIndex={rowIndex}
                        language={this.props.i18n.language}
                        uglyMode={this.state.isFullViewMode}
                        workflowReadOnly={this.props.workFlowResetStateDisabled(key, item, readOnly)}
                        value={item[key] as string ? item[key] as string : ''}
                        onChange={(newValue) => this.onDataChanged(key, id, newValue)}
                        readOnly={readOnly ? readOnly : this.props.isCellReadOnly(key, item, readOnly)}
                        inEditMode={this.props.inEditMode}
                        setActiveRow={() => item._id && this.setActiveRow(item._id)}
                        clearActiveRow={this.clearActiveRow}
                        editDialogHeader={{
                            dialogTitle: this.props.t('BASEOPTIONS:EDIT_BASIC_OPTION'),
                            previewAiHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_HEADER'),
                            previewAiSubHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_SUB_HEADER'),
                            previewAiOriginal: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_ORIGINAL'),
                            previewAiPreview: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_PREVIEW'),
                            previewAiConfirm: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM'),
                            previewAiCancel: this.props.t('GLOBAL:CANCEL'),
                            previewAiConfimChange: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM_CHANGES'),
                            previewAiGrammarSubHeader: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_GRAMMAR_SUB_HEADER'),
                            tooltipAiTranslate: this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP'),
                            tooltipAiGrammar:this.props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP_GRAMMAR'),
                            editingColumn: this.state.visibleColumns[columnIndex - 1].label,
                            optionCode: item.optionCode ? item.optionCode as string : "",
                            // @ts-ignore
                            optionType: item.optionType ? item.optionType.optionType as string : "",
                            pdipText: item.pdiptext as string
                        }}
                        onTriggerClick={async () => {
                            let allowed = readOnly;
                            if (!readOnly) {
                                allowed = !((item as any).statusView.statusView !== WorkflowStatusDtoType.MAINTENANCE);
                            }
                            if (item.id && this.props.inEditMode && allowed) {
                                if (this.props.client) {
                                    const res = await this.props.client.getOptionTypeUsingId(item.id);
                                    this.props.onTriggerClick(res as string[]);
                                }
                            }

                        }}
                        isEditor={true}
                        isFromBaseOptions={true}
                        client={this.props.client}
                        postOptions={this.props.resourceLinks}
                        apiMethod={(client, postOptions) => client.POST_optionBasicAWSTranslate(postOptions)}
                        grammarApiMethod={(client, unusedPostOptions) =>
                            this.props.baseLinks
                                ? client.POST_awsBedrockGrammar(this.props.baseLinks)
                                : { execute: () => Promise.reject("Links not loaded") }
                        }
                        aiGeneratedShorttext={aiGeneratedShorttext}
                        aiGeneratedLongtext={aiGeneratedLongtext}
                    />
                </div>
            );
        }
    }

    public render() {
        let data: any[] = this.appendAdditionalPropertiesToData();
        let filteredData = this.filterData(data);
        data = filteredData.slice((this.state.currentPage - 1) * this.state.numberOfItems, this.state.currentPage * this.state.numberOfItems);
        const columnsForHeightMeasurement = this.state.visibleColumns.filter(col => [ShorttextKey, LongtextKey, CommentKey].includes(col.key));

        return (
            <div className={"position--relative"} data-cy="option-basic-table">
                {/* ========== REACT VIRTUALIZED GRID ============= */}
                <div id='grid-container'>
                    <AutoSizer disableHeight={true}>
                        {({width}) => (
                            <React.Fragment>
                                {/* This div forces the header to be in sync with the body when scrolling
                              Also, displays the message for 'no data'*/}
                                <div
                                    className="grid-overlay-div"
                                >
                                    {data.length === 0 &&
                                        <div>{this.props.t("NO_DATA")}</div>
                                    }
                                </div>
                                <ReactVirtualizedGrid
                                    id={virtualizedTableId}
                                    ref={(ref) => this.gridRef = ref}
                                    rowCount={data.length > 0 ? data.length + 1 : 2}
                                    rowHeight={(index) => this.getRowHeight(index, data, columnsForHeightMeasurement)}
                                    width={width}
                                    height={window.innerHeight - 176}
                                    columnCount={this.getColumnCount()}
                                    estimatedColumnSize={150}
                                    cellRenderer={(props: GridCellProps) => this.renderGridCell(props, data, filteredData)}
                                    columnWidth={(index) => this.getColumnWidth(index)}
                                    fixedRowCount={1}
                                    overscanRowCount={5}
                                    overscanColumnCount={5}
                                />
                            </React.Fragment>
                        )}
                    </AutoSizer>
                </div>
                {/* ========== PAGINATION TABS ============= */}
                <div className='virtualized-table-pagination-footer' >
                    <TableFooterActions
                        t={this.props.t}
                        visibleColumns={sortOBColumnsDropdown(this.state.rowVisibleList)}
                        onChange={this.handleTableMenuChange}
                        onChangeViewMode={() => this.setState({isFullViewMode: !this.state.isFullViewMode})}
                        isFullViewMode={this.state.isFullViewMode}
                        pdipFilterValue={(this.state.filterValue[PDiPFlag] || PDiPFilterEnum.SHOW_ALL) as PDiPFilterEnum}
                        pccdFilterValue={(this.state.filterValue[PCCDFlag] || PCCDFilterEnum.SHOW_ALL) as PCCDFilterEnum}
                        onFilterChange={(key, val) => this.onFilterChange({...this.state.filterValue, [key]: val})}
                        columnLabels={this.props.columnsData}
                    />
                    <TablePagination
                        numberOfItems={this.state.numberOfItems}
                        setNumberOfItems={this.setNumberOfItems}
                        dataLength={this.state.data.length}
                        filteredDataLength={filteredData.length}
                        hasScrollBtn={true}
                        t={this.props.t}
                        currentPage={this.state.currentPage}
                        onPageChange={(e, c) =>
                            this.setState(
                                {currentPage: c.activePage as number},
                                scrollGridToTop
                            )}
                    />
                </div>

                {/*this div is used to measure the height of the row before rendering*/}
                {this.state.isFullViewMode &&
                    <div id='hidden-long-text-box' className='display--flex flex-direction--column'>
                        <p id='textarea-en' />
                        <p id='textarea-de' />
                    </div>
                }
            </div>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    optionBasicTableSort: selectOptionBasicTableSort(state),
    dynTableSelectedIdList: selectVirtualizedTableSelectedIdList(state),
    dynTabelRollback: selectVirtualizedTableRollback(state),
    dynTableReload: selectVirtualizedTableReload(state),
    client: selectClient(state),
    baseLinks: state.general.baseLinks
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IMapDispatchToProps => ({
    setSelectedIdLists: (ids: string[]) => dispatch(GeneralActions.DynTableSetSelectedIdList(ids)),
    setTableSort: (sort: ITableSort) => dispatch(BaseOptionViewActions.SetOptionBasicTableSort(sort)),
    setTableRollBack: (flag: boolean) => dispatch(GeneralActions.DynTableRollBack(flag)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate(["COLUMN_FACTORY", "GLOBAL", "EXPORT", "TRANSLATION"]),
    connect<IMapStateToProps>(mapStateToProps, mapDispatchToProps)
)(BaseOptionsTable);



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsTable.tsx